import React, { useState } from "react";
import {
  FaPlusCircle,
  FaPencilAlt,
  FaTrashAlt,
  FaFileAlt,
} from "react-icons/fa";
import CreateTreatmentPlan from "./CreateTreatmentPlan"; // Import the CreateTreatmentPlan component
import { useNavigate } from "react-router-dom";

export default function TreatmentPlan() {
  // Using "treatmentPlans" as the main state with some initial data
  const [treatmentPlans, setTreatmentPlans] = useState([
    {
      id: 1,
      patientName: "Ramesh Kumar",
      title: "Chemotherapy",
      startDate: "2023-10-01",
      endDate: "2023-12-01",
      status: "Ongoing",
      doctorNotes:
        "Patient responding well to the treatment. Monitor vitals during sessions.",
      medications: [
        { name: "Cisplatin", dosage: "50mg", frequency: "Once Daily" },
        { name: "Ondansetron", dosage: "4mg", frequency: "Twice Daily" },
      ],
    },
    {
      id: 2,
      patientName: "Priya Sharma",
      title: "Radiation Therapy",
      startDate: "2023-07-01",
      endDate: "2023-09-01",
      status: "Completed",
      doctorNotes:
        "Treatment successful with no side effects. Patient advised for periodic follow-ups.",
      medications: [],
    },
    {
      id: 3,
      patientName: "Arjun Patel",
      title: "Physical Therapy",
      startDate: "2023-11-10",
      endDate: "2023-12-15",
      status: "Ongoing",
      doctorNotes:
        "Daily physiotherapy sessions required for recovery. Patient showing progress.",
      medications: [
        { name: "Ibuprofen", dosage: "200mg", frequency: "Twice Daily" },
      ],
    },
  ]);

  // Mock patient and procedure master data
  const mockPatient = {
    id: "PAT-001",
    name: "John Doe",
    age: 45,
    gender: "Male",
  };

  const procedureMasterData = [
    {
      id: "PROC-001",
      procedureName: "Appendectomy",
      departmentID: "General Surgery",
      baseCost: 15000,
    },
    {
      id: "PROC-002",
      procedureName: "Angioplasty",
      departmentID: "Cardiology",
      baseCost: 30000,
    },
    {
      id: "PROC-003",
      procedureName: "Cholecystectomy",
      departmentID: "General Surgery",
      baseCost: 18000,
    },
  ];

  const navigate = useNavigate();

  // Function to add a new treatment plan from the modal
  const handleCreateTreatment = (newTreatment) => {
    setTreatmentPlans([...treatmentPlans, { id: Date.now(), ...newTreatment }]);
    closeModal();
  };

  // Function to delete a treatment plan
  const handleDeletePlan = (id) => {
    setTreatmentPlans(treatmentPlans.filter((plan) => plan.id !== id));
  };

  // Function to edit a treatment plan (placeholder)
  const handleEditPlan = (plan) => {
    console.log("Edit plan:", plan);
    // Implement your edit logic here (e.g., navigate to an edit screen)
  };

  // Function to track a treatment plan
  const handleTrackPlan = (id) => {
    navigate(`/new/treatment/${id}`);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const addNewTreatment = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Header */}

      {/* Treatment Plans Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {treatmentPlans.length > 0 ? (
          treatmentPlans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white shadow-lg rounded-lg overflow-hidden transform transition hover:scale-105 hover:shadow-2xl"
            >
              {/* Plan Header */}
              <div className="p-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  {plan.patientName}
                </h2>
                <p className="text-gray-600">{plan.title}</p>
                <div className="mt-2">
                  <p className="text-gray-600">
                    <span className="font-bold">Start:</span> {plan.startDate}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-bold">End:</span> {plan.endDate}
                  </p>
                </div>
                <span
                  className={`inline-block mt-3 px-3 py-1 text-sm font-semibold rounded-full ${
                    plan.status === "Ongoing"
                      ? "bg-blue-500 text-white"
                      : "bg-green-500 text-white"
                  }`}
                >
                  {plan.status}
                </span>
              </div>

              {/* Medications Section */}
              {plan.medications.length > 0 && (
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                  <h3 className="text-md font-semibold text-gray-800 mb-2">
                    Medications
                  </h3>
                  <ul>
                    {plan.medications.map((med, index) => (
                      <li
                        key={index}
                        className="flex justify-between text-gray-600"
                      >
                        <span>{med.name}</span>
                        <span>
                          {med.dosage}, {med.frequency}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Doctor's Notes */}
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <h3 className="text-md font-semibold text-gray-800 mb-2">
                  Doctor's Notes
                </h3>
                <p className="text-gray-600">{plan.doctorNotes}</p>
              </div>

              {/* Action Buttons */}
              <div className="p-4 flex justify-around border-t border-gray-200">
                <button
                  onClick={() => handleEditPlan(plan)}
                  className="flex items-center space-x-2 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition"
                >
                  <FaPencilAlt />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDeletePlan(plan.id)}
                  className="flex items-center space-x-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                >
                  <FaTrashAlt />
                  <span>Delete</span>
                </button>
                <button
                  onClick={() => handleTrackPlan(plan.id)}
                  className="flex items-center space-x-2 p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition"
                >
                  <FaFileAlt />
                  <span>Track</span>
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-600 text-lg">
            No treatment plans available.
          </div>
        )}
      </div>

      {/* Modal for Creating Treatment Plan */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-4xl p-6 rounded-lg shadow-lg relative">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                Add New Treatment Plan
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-800 text-3xl focus:outline-none"
              >
                &times;
              </button>
            </div>
            <div className="overflow-y-auto max-h-[80vh]">
              <CreateTreatmentPlan
                onSavePlan={handleCreateTreatment}
                patient={mockPatient}
                proceduresMaster={procedureMasterData}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
